import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { Map as MapIcon, Database, Users, Key, Download, Upload } from 'lucide-react';

const Dashboard = ({ activeOrg, onGoToMap }: { activeOrg?: any, onGoToMap?: () => void }) => {
    const [orgs, setOrgs] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const loadAdminData = () => {
        axios.get(`${API_URL}/admin/organizations`, config).then(res => {
            if (activeOrg) {
                setOrgs(res.data.filter((o: any) => o.id === activeOrg.id));
            } else {
                setOrgs(res.data);
            }
        });
        axios.get(`${API_URL}/admin/users`, config).then(res => {
            if (activeOrg) {
                setUsers(res.data.filter((u: any) => u.org_name === activeOrg.name));
            } else {
                setUsers(res.data);
            }
        });
    };

    useEffect(() => { loadAdminData(); }, [activeOrg]);

    const handleExport = async (orgId: number, orgName: string) => {
        try {
            const res = await axios.get(`${API_URL}/admin/export/${orgId}`, config);
            const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `backup_${orgName}_${new Date().toISOString().split('T')[0]}.json`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) { alert("Błąd podczas generowania kopii zapasowej."); }
    };

    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const jsonData = JSON.parse(event.target?.result as string);
                await axios.post(`${API_URL}/admin/import`, jsonData, config);
                alert("Dane zostały pomyślnie zaimportowane!");
                loadAdminData();
            } catch (err) { alert("Błąd importu: Nieprawidłowy format pliku."); }
        };
        reader.readAsText(file);
    };

    const handleChangePassword = async (userId: number, email: string) => {
        const newPassword = window.prompt(`Wprowadź nowe hasło dla użytkownika ${email}:`);
        if (!newPassword) return;
        
        try {
            await axios.put(`${API_URL}/admin/users/${userId}/password`, { password: newPassword }, config);
            alert("Hasło zostało pomyślnie zmienione.");
        } catch (err) {
            alert("Wystąpił błąd podczas zmiany hasła.");
        }
    };

    return (
        <div className="dashboard-container">
            {/* ── HEADER ── */}
            <div className="dashboard-header">
                <h1 className="dashboard-title">
                    {activeOrg ? `Panel Organizacji: ${activeOrg.name}` : 'Panel Administratora Systemu'}
                </h1>

                {onGoToMap && (
                    <button onClick={onGoToMap} className="dashboard-map-btn">
                        <MapIcon size={18} /> Otwórz Mapę Sieci
                    </button>
                )}
            </div>

            {/* ── KARTY ── */}
            <div className="dashboard-grid">
                {/* Karta 1: Szczegóły Organizacji */}
                <div className="dashboard-card">
                    <div className="dashboard-card-header">
                        <Database size={20} color="#3b82f6" />
                        <h2>Szczegóły Organizacji</h2>
                    </div>
                    
                    <div className="dashboard-table-wrapper">
                        <table className="dashboard-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>ID</th>
                                    <th>Organizacja</th>
                                    <th style={{ textAlign: 'right' }}>Akcje</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orgs.map(o => (
                                    <tr key={o.id}>
                                        <td style={{ color: '#6b7280', fontWeight: 600 }}>{o.id}</td>
                                        <td>
                                            <div style={{ fontWeight: 600, color: '#111827' }}>{o.name}</div>
                                            <div className="coord-cell">{o.latitude.toFixed(3)}, {o.longitude.toFixed(3)}</div>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button
                                                onClick={() => handleExport(o.id, o.slug)}
                                                className="btn-backup"
                                            >
                                                <Download size={13} /> Backup
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="dashboard-import-box">
                        <div className="dashboard-import-title">
                            <Upload size={16} />
                            <h4>Przywróć dane z pliku</h4>
                        </div>
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleImport}
                            className="dashboard-file-input"
                        />
                    </div>
                </div>

                {/* Karta 2: Pracownicy / Użytkownicy */}
                <div className="dashboard-card">
                    <div className="dashboard-card-header">
                        <Users size={20} color="#6366f1" />
                        <h2>Pracownicy / Użytkownicy</h2>
                    </div>

                    <div className="dashboard-table-wrapper">
                        <table className="dashboard-table">
                            <thead>
                                <tr>
                                    <th>Użytkownik</th>
                                    <th className="hide-on-mobile">Organizacja</th>
                                    <th style={{ textAlign: 'right' }}>Akcje</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => (
                                    <tr key={u.id}>
                                        <td>
                                            <div className="email-cell">{u.email}</div>
                                            <div style={{ marginTop: '3px' }}>
                                                <span className="role-badge">{u.role}</span>
                                            </div>
                                        </td>
                                        <td className="hide-on-mobile" style={{ color: '#4b5563', fontSize: '13px' }}>
                                            {u.org_name}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button
                                                onClick={() => handleChangePassword(u.id, u.email)}
                                                className="btn-password"
                                            >
                                                <Key size={13} /> Hasło
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;